/**
 * Get the RiseAI base URL from environment variable
 * Defaults to https://riseai-dev.netrise.io if not set
 */
const getRiseAIBaseUrl = () => {
  return process.env.REACT_APP_RISEAI_HOST || 'https://riseai-dev.netrise.io';
};

/**
 * Get the chat endpoint URL
 */
export const getChatEndpointUrl = () => {
  let baseUrl;
  if (process.env.NODE_ENV === 'development' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    // Use relative path for proxy - /run_sse will be proxied
    baseUrl = '';
  } else {
    baseUrl = getRiseAIBaseUrl();
  }

  // Build the chat endpoint URL: /run_sse (proxied in localhost, direct in development and production)
  return baseUrl ? `${baseUrl}/run_sse` : '/run_sse';
};

/**
 * Get the feedback endpoint base URL
 */
export const getFeedbackBaseUrl = () => {
  // In development (localhost), use proxy path to avoid CORS
  if (process.env.NODE_ENV === 'development' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return ''; // This will be proxied by setupProxy.js
  }
  // In development and production, use direct URL
  return getRiseAIBaseUrl();
};

/**
 * Submit or update feedback for a message
 * @param {string} sessionId - The chat session ID
 * @param {string} eventId - The event/message ID
 * @param {string} rating - 'thumbs_up' or 'thumbs_down'
 * @param {string} reason - Optional free-text reason
 * @param {string} jwtToken - JWT authentication token
 * @param {string} assetId - Optional asset ID for X-Asset-Id header
 * @returns {Promise<Object>} The feedback record
 */
export const submitFeedback = async (sessionId, eventId, rating, reason = '', jwtToken, assetId = '') => {
  const baseUrl = getFeedbackBaseUrl();
  const url = baseUrl ? `${baseUrl}/feedback` : '/feedback';

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Authorization': `Bearer ${jwtToken}`,
      ...(assetId && { 'X-Asset-Id': assetId })
    },
    body: JSON.stringify({
      session_id: sessionId,
      event_id: eventId,
      rating,
      reason: reason || ''
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP error! status: ${response.status}` }));
    throw new Error(errorData.error || `Failed to submit feedback: ${response.status}`);
  }

  return response.json();
};

/**
 * Get all feedback for a session
 * @param {string} sessionId - The chat session ID
 * @param {string} jwtToken - JWT authentication token
 * @param {string} assetId - Optional asset ID for X-Asset-Id header
 * @returns {Promise<Array>} Array of feedback records
 */
export const getSessionFeedback = async (sessionId, jwtToken, assetId = '') => {
  if (!jwtToken) {
    throw new Error('JWT token is required for getSessionFeedback');
  }

  const baseUrl = getFeedbackBaseUrl();
  const url = baseUrl ? `${baseUrl}/feedback?sid=${encodeURIComponent(sessionId)}` : `/feedback?sid=${encodeURIComponent(sessionId)}`;

  const headers = {
    'X-Authorization': `Bearer ${jwtToken}`,
    ...(assetId && { 'X-Asset-Id': assetId })
  };

  const response = await fetch(url, {
    method: 'GET',
    headers: headers,
    credentials: 'include'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP error! status: ${response.status}` }));
    throw new Error(errorData.error || `Failed to get feedback: ${response.status}`);
  }

  return response.json();
};

/**
 * Delete feedback for a specific event
 * @param {string} sessionId - The chat session ID
 * @param {string} eventId - The event/message ID
 * @param {string} jwtToken - JWT authentication token
 * @param {string} assetId - Optional asset ID for X-Asset-Id header
 * @returns {Promise<Object>} The deleted feedback record (with rating and reason)
 */
export const deleteFeedback = async (sessionId, eventId, jwtToken, assetId = '') => {
  // First, get the feedback to retrieve rating and reason before deleting
  let feedbackToDelete = null;
  try {
    const allFeedback = await getSessionFeedback(sessionId, jwtToken, assetId);
    feedbackToDelete = allFeedback.find(f => f.event_id === eventId) || null;
  } catch (error) {
    // If we can't fetch feedback, continue with deletion anyway
  }

  const baseUrl = getFeedbackBaseUrl();
  const url = baseUrl 
    ? `${baseUrl}/feedback?sid=${encodeURIComponent(sessionId)}&eid=${encodeURIComponent(eventId)}`
    : `/feedback?sid=${encodeURIComponent(sessionId)}&eid=${encodeURIComponent(eventId)}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'X-Authorization': `Bearer ${jwtToken}`,
      ...(assetId && { 'X-Asset-Id': assetId })
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP error! status: ${response.status}` }));
    throw new Error(errorData.error || `Failed to delete feedback: ${response.status}`);
  }

  // Return the deleted feedback record with rating and reason, or the deletion status if we couldn't fetch it
  if (feedbackToDelete) {
    return {
      ...feedbackToDelete,
      status: 'deleted'
    };
  }

  return {
    status: 'deleted',
    eid: eventId,
    sid: sessionId
  };
};
